/**
 * The numbers, for admins: the readers behind is_admin() (migrations 0014, 0018, 0019) and
 * card_rank (0005/0008 — the database lets anon call it; /stats keeps it behind the same door
 * as a product choice). A factory over a narrow client, like src/api/account.ts, so the RPC
 * names, their SQL argument names and the SQLSTATE-to-sentence mapping are proven with a fake.
 *
 * The readers never resolve a session: supabase-js sends the persisted one on its own, and
 * signing a stranger in just to refuse them would mint an auth user (and, through the
 * on_auth_user_created trigger, an actor row that counts in stats_totals). markInternal() is
 * the one exception — an anonymous tour browser has no session until it asks for one, and the
 * flag it sets is what it came for.
 *
 * DAU = distinct actors (browser profiles) with a card session that day; account_dau = the
 * subset behind a real account. Days are UTC.
 */
import type { Session } from '@supabase/supabase-js';
import { ensureSession, supabase } from '../backend/supabase';

/** stats_daily(days): one row per UTC day, oldest first; every column is a count (0019:429-489). */
export interface DailyRow {
  day: string;
  dau: number;
  new_actors: number;
  account_dau: number;
  sessions: number;
  cards_played: number;
  comments: number;
  cards_added: number;
}

/** stats_totals(): one object of counts (0019:491-517). */
export interface Totals {
  actors: number;
  accounts: number;
  handles: number;
  live_cards: number;
  linked_cards: number;
  comments: number;
  sessions_all_time: number;
}

/**
 * retention_daily(days, via): one row per UTC day (0019:307-325). Fractions are 0..1; every
 * numeric column is null when there was nothing to average, and d1/d1_n are null for today AND
 * yesterday by design — the day's next UTC day must be complete (0019:414-420).
 */
export interface RetentionRow {
  day: string;
  first_visits_browser: number;
  pos0_hold: number | null;
  pos0_n: number;
  reached_end: number | null;
  visits: number;
  median_depth: number | null;
  back_taps_per_visit: number | null;
  share_taps: number;
  likes: number;
  idle_share_external: number | null;
  idle_share_hosted: number | null;
  webview_share: number | null;
  boot_ms_p50: number | null;
  d1: number | null;
  d1_n: number | null;
}

/** metric_snapshot_read(p_name) (0018:32-51): value is a fraction 0..1 or a count; the metric's name says which. */
export interface BaselineRow {
  name: string;
  metric: string;
  value: number | null;
  n: number | null;
  note: string | null;
  taken_at: string;
}

/** card_rank(window_days) (0008:28-47): live cards in feed order; nothing a card reports about itself is in it (docs/RANKING.md). */
export interface CardRankRow {
  card_id: string;
  rank: number;
  /** 'ranked' | 'explore' | 'tail' — kept as string so a JSON fixture types without a cast. */
  slot: string;
  score: number | null;
  editorial_position: number | null;
  demotion: number;
  impressions: number;
  actors: number;
  votes: number;
  voters: number;
  n_eff: number;
  hold_rate: number;
  hold_ucb: number;
  pool_hold_rate: number;
  bounce_rate: number | null;
  crash_rate: number | null;
  share_referrers: number;
  lifetime_votes: number;
}

/**
 * loop_funnel(days) (0021): the creation loop per UTC day, and a total row with day null that
 * counts distinct actors over the whole window (so it is not the sum of the days). People only:
 * not @rand, the house, probes or headless clients.
 */
export interface LoopRow {
  day: string | null;
  brief_copy: number;
  make_open: number;
  upload_actors: number;
  uploads: number;
  fit_ok: number;
  live: number;
  reached: number;
  stats_open: number;
  complete: number;
}

/** bench_summary() (0020): what is live, what waits, how long the oldest has waited; the cap is reported, never enforced. */
export interface BenchSummary {
  live: number;
  bench: number;
  hidden_7d: number;
  oldest_bench_age_h: number | null;
  votes_per_day_7d: number;
  cap: number;
}

export type StatsResult<T> = { ok: true; rows: T } | { ok: false; error: string };

export const NO_BACKEND_ERROR = 'This build has no backend.';
export const ADMINS_ONLY = 'Admins only.';
export const NO_SESSION = 'Could not sign this browser in. Try again in a minute.';

/** The slice of supabase-js the readers touch (the same shape as AccountClient.rpc), so a test can hand in a fake. */
export interface StatsClient {
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: { code?: string; message: string } | null }>;
}

export interface StatsApi {
  daily(days?: number): Promise<StatsResult<DailyRow[]>>;
  totals(): Promise<StatsResult<Totals>>;
  /** via: 'pilot' | 'landing' | 'endcard', or null for every door. */
  retention(days: number, via: string | null): Promise<StatsResult<RetentionRow[]>>;
  baseline(): Promise<StatsResult<BaselineRow[]>>;
  cardRank(windowDays: number): Promise<StatsResult<CardRankRow[]>>;
  loopFunnel(days: number): Promise<StatsResult<LoopRow[]>>;
  benchSummary(): Promise<StatsResult<BenchSummary>>;
  /** set_internal(true) for the signed-in actor; signs an anonymous browser in first. uid = the auth user it marked. */
  markInternal(): Promise<{ ok: true; uid: string } | { ok: false; error: string }>;
}

/**
 * 42501 is 'admin only' from the readers AND 'permission denied' for a call with no session at
 * all; one sentence covers both. The page picks its door from the account, never from this.
 */
export function rpcError(e: { code?: string; message: string }): string {
  return e.code === '42501' ? ADMINS_ONLY : e.message || 'The server refused that.';
}

export function createStatsApi(client: StatsClient | null, resolve: () => Promise<Session | null>): StatsApi {
  async function call(fn: string, args?: Record<string, unknown>): Promise<{ ok: true; data: unknown } | { ok: false; error: string }> {
    if (!client) return { ok: false, error: NO_BACKEND_ERROR };
    const { data, error } = await client.rpc(fn, args);
    return error ? { ok: false, error: rpcError(error) } : { ok: true, data };
  }
  async function rows<R>(fn: string, args?: Record<string, unknown>): Promise<StatsResult<R[]>> {
    const r = await call(fn, args);
    return r.ok ? { ok: true, rows: Array.isArray(r.data) ? (r.data as R[]) : [] } : r;
  }
  return {
    daily: (days = 14) => rows<DailyRow>('stats_daily', { days }),
    totals: async () => {
      const r = await call('stats_totals');
      if (!r.ok) return r;
      return r.data && typeof r.data === 'object' ? { ok: true, rows: r.data as Totals } : { ok: false, error: 'stats_totals returned nothing.' };
    },
    retention: (days, via) => rows<RetentionRow>('retention_daily', { days, via }),
    baseline: () => rows<BaselineRow>('metric_snapshot_read', { p_name: 'baseline' }),
    cardRank: (windowDays) => rows<CardRankRow>('card_rank', { window_days: windowDays }),
    loopFunnel: (days) => rows<LoopRow>('loop_funnel', { p_days: days }),
    benchSummary: async () => {
      const r = await call('bench_summary');
      if (!r.ok) return r;
      return r.data && typeof r.data === 'object' ? { ok: true, rows: r.data as BenchSummary } : { ok: false, error: 'bench_summary returned nothing.' };
    },
    async markInternal() {
      if (!client) return { ok: false, error: NO_BACKEND_ERROR };
      const s = await resolve();
      if (!s) return { ok: false, error: NO_SESSION }; // 429 on anonymous sign-in, or offline
      const r = await call('set_internal', { flag: true });
      return r.ok ? { ok: true, uid: s.user.id } : r;
    },
  };
}

/** The app's stats API, bound to the configured client (the cast mirrors src/api/account.ts:259). */
export const stats = createStatsApi(supabase as unknown as StatsClient | null, ensureSession);

/** The shape AccountSheet's StatsPanel reads; unchanged for it. */
export async function fetchStats(days = 14): Promise<{ daily: DailyRow[]; totals: Totals | null } | { error: string }> {
  const [d, t] = await Promise.all([stats.daily(days), stats.totals()]);
  if (!d.ok) return { error: d.error };
  return { daily: d.rows, totals: t.ok ? t.rows : null };
}
